import React, { useState } from 'react';
import { Box, TextField, Button, CircularProgress } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSnackbar } from '../../../utils/useSnackbar';
import { breakpoints } from '../../../theme/theme';
import styles from '../../../styles/login/rightSection.module.scss';
import { requestSignupVerification } from '../../../utils/api/signup_apis';
import { STATUS } from '../../../utils/api/sharedfunction';

const SignupEmailSchema = z.object({
  email: z.string().email('Please enter a valid email address').min(1, 'Email is required'),
});

const sharedTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '0.5rem',
    bgcolor: '#fff',
    fontSize: '1rem',
    color: '#0f172a',
    transition: 'all 0.2s',
    boxShadow: 'none',
    border: 'none',
    '& fieldset': {
      borderRadius: '0.5rem',
      borderColor: '#CBD5E1',
    },
    '&:hover fieldset': {
      borderColor: '#3b82f6',
      borderRadius: '0.5rem',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
      borderWidth: 2,
      borderRadius: '0.5rem',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#CBD5E1',
    borderWidth: 1,
    borderRadius: '0.5rem',
    transition: 'border-color 0.2s, border-width 0.2s, border-radius 0.2s',
  },
  '& .MuiOutlinedInput-input': {
    height: '1.2rem',
    [`@media (min-width:${breakpoints.xl}px)`]: {
      height: '1.625rem',
    },
  },
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    backgroundColor: '#fff',
    fontSize: '0.7rem',
    [`@media (min-width:${breakpoints.xl}px)`]: {
      fontSize: '1rem',
    },
    color: '#0f172a',
    lineHeight: '1.5rem',
    fontWeight: 400,
    transition: 'all 0.2s',
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#64748b',
    opacity: 1,
    fontSize: '13px',
    lineHeight: '1rem',
    fontWeight: 400,
  },
  '& .MuiFormHelperText-root': {
    fontSize: '0.7rem',
    lineHeight: '1rem',
    color: '#ef4444',
  },
};

export default function SignupStep1Email({ onSuccess }) {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(SignupEmailSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const response = await requestSignupVerification({ email: data.email.trim() });

      if (response.status === STATUS.SUCCESS) {
        reset({ email: '' });
        onSuccess?.();
      } else {
        showSnackbar(response.error || 'Failed to send verification email. Please try again.', 'error');
      }
    } catch (err) {
      showSnackbar(err?.error || 'Failed to send verification email. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box className={styles['form-header']}>
        <h6 className={styles['form-title']}>Create your account</h6>
        <p className={styles['form-subtitle']}>
          Enter your work email and we&apos;ll send you a verification link. Personal email domains (e.g. Gmail, Yahoo) are not allowed.
        </p>
      </Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} className={styles['form']}>
        <Box className={styles['form-field']}>
          <h6 htmlFor="signup-email" className={styles['field-label']}>
            Email address
          </h6>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id="signup-email"
                type="email"
                placeholder="Enter your work email"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.email}
                helperText={errors.email?.message}
                autoFocus
                sx={sharedTextFieldSx}
              />
            )}
          />
        </Box>
        <Button
          type="submit"
          variant="primaryBtn"
          fullWidth
          disabled={loading}
          className={styles['submit-button']}
        >
          {loading ? (
            <CircularProgress size={20} className={styles['submit-button-loading']} />
          ) : (
            'Send verification link'
          )}
        </Button>
      </Box>
    </>
  );
}
